import React, {
	useState,
	useRef,
	useCallback,
	useMemo,
	forwardRef,
	useImperativeHandle
} from "react";
import AudioInput from "./AudioInput";
import { ErrorIcon } from "../Icons/Interface";

interface LongAnswerProps {
	onRecordingComplete?: (audioBlob: Blob) => Promise<void>;
	onRecordingCancel?: () => Promise<void>;
	onTextCancel?: () => Promise<void>;
	onTextComplete?: (answer: string) => Promise<void>;
	onTextChange?: (answer: string) => void;
	label?: string;
	value?: string;
	className?: string;
	enableAudio?: boolean;
	onValidate?: (value: string) => boolean;
	validationText?: string;
}

export interface LongAnswerInputHandle {
	triggerValidate: () => boolean;
}

const noopPromise = async (): Promise<void> => {};

const LongAnswerInput = forwardRef<LongAnswerInputHandle, LongAnswerProps>(
	(
		{
			onRecordingComplete = noopPromise,
			onRecordingCancel = noopPromise,
			onTextCancel = noopPromise,
			onTextComplete = noopPromise,
			onTextChange,
			label = "",
			value = "",
			className = "text-primary-active border bg-primary-background border-primary-active",
			enableAudio = true,
			onValidate: userOnValidate = (noop: string) => true,
			validationText = ""
		},
		ref
	) => {
		const textareaRef = useRef(null);
		const [textValue, setTextValue] = useState<string>(value);
		const [isFocused, setIsFocused] = useState<boolean>(false);
		const [isClientFailure, setIsClientFailure] = useState<boolean>(false);
		const [invalidMessage, setInvalidMessage] =
			useState<string>(validationText);
		const [hasTriggeredValidation, setHasTriggeredValidation] =
			useState<boolean>(false);
		const isInitialValue = textValue === value;

		useImperativeHandle(ref, () => ({
			triggerValidate: () => {
				setHasTriggeredValidation(true);
				return onValidate(textValue);
			}
		}));

		const tailwindColor = {
			active: "#f7feff",
			accent: "#29303E",
			inactive: "#5f5766",
			background: "#121929"
		};
		// CSS for webkit scrollbars (needs to be inserted)
		//
		const scrollbarCss = `
    .scrollbar-thin::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    
    .scrollbar-trackColor::-webkit-scrollbar-track {
        background: ${tailwindColor.background};
        border-radius: 3px;
    }
    
    .scrollbar-thumbColor::-webkit-scrollbar-thumb {
        background: ${tailwindColor.active};
        border-radius: 3px;
		cursor: pointer;
    }
    
    .scrollbar-thumbHoverColor::-webkit-scrollbar-thumb:hover {
        background: ${tailwindColor.inactive};
    }
  `;

		// Safe wrapper function for onValidate that only handles errors during user interactions
		const onValidate = useCallback(
			(value: string): boolean => {
				try {
					const isValid = userOnValidate(value);
					return isValid;
				} catch (err) {
					console.log("Client Validate Threw Exception:", err);
					setInvalidMessage("Call Support - Client Validate");
					setIsClientFailure(true);
					return false;
				}
			},
			[userOnValidate]
		);

		// Track if validation is currently failing
		const isInvalid = useMemo(() => {
			// Show validation errors if: 1) value changed from initial, OR 2) triggerValidate was called
			return (
				(!isInitialValue || hasTriggeredValidation) && !onValidate(textValue)
			);
		}, [isInitialValue, hasTriggeredValidation, textValue, onValidate]);

		const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
			e: React.ChangeEvent<HTMLTextAreaElement>
		) => {
			setTextValue(e.target.value);
			onTextChange?.(e.target.value);
		};

		const handleFocus = (): void => {
			setIsFocused(true);
		};

		const handleBlur = (): void => {
			setIsFocused(false);
			// Handle onTextComplete asynchronously without blocking blur
			onTextComplete(textValue).catch((error) => {
				console.error(error);
			});
		};

		const clearInput = (): void => {
			setTextValue("");
		};

		const handleKeypress = async (
			event: React.KeyboardEvent<HTMLTextAreaElement>
		): Promise<void> => {
			console.log(
				`CustomInput Keydown Called key:[${event.ctrlKey ? "Ctrl+" : ""}${event.altKey ? "Alt+" : ""}${event.shiftKey ? "Shift+" : ""}${event.key}]  Target:[${label}]`
			);
			if (event.key === "Escape") {
				clearInput();
				event.stopPropagation(); //StopBubbling event as it was handled.
				try {
					await onTextCancel();
				} catch (error) {
					console.error(error);
				}
			}

			if (event.ctrlKey === true && event.key === "Enter") {
				event.stopPropagation(); //StopBubbling event as it was handled.
				try {
					await onTextComplete(textValue);
				} catch (error) {
					console.error(error);
				}
			}
		};

		return (
			<>
				<div
					className={`relative ${className} border rounded-3xl mt-10 p-4 transition-all 
				${
					isInvalid
						? "focus-within:border-error-active border-error-inactive"
						: "focus-within:border-primary-active border-primary-inactive"
				}				
				`}
				>
					{label && (
						<label
							className={`absolute text-primary transition-all duration-300 pointer-events-none ${
								isFocused || textValue
									? "text-sm -top-2.5 left-6 px-1 h-6 rounded bg-primary-background truncate lineclamp-1 max-w-[90%]"
									: //"text-sm -top-2.5 left-0 px-1 rounded bg-primary-background line-clamp-2 max-w-[100%]"
										"text-base top-3 left-6 bg-transparent"
							}`}
						>
							{label}
						</label>
					)}

					<textarea
						ref={textareaRef}
						onKeyDown={handleKeypress}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onChange={onChange}
						className={`scrollbar-thin scrollbar-trackColor scrollbar-thumbColor hover:scrollbar-thumbHoverColor 
								w-full h-full 																 
								focus:outline-none bg-primary-background resize-none`}
						value={textValue}
					/>
					<style>{scrollbarCss}</style>

					{enableAudio && (
						<div className="flex flex-row justify-end p-0">
							<div>
								<AudioInput
									recordingColor="#00FF00"
									onRecordingComplete={onRecordingComplete}
									onRecordingCancel={onRecordingCancel}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Validation message container with fixed height */}
				<div
					className={`cursor-default flex items-center justify-start text-sm px-6 mt-1 transition-opacity duration-200 min-h-6 ${
						isInvalid || isClientFailure ? "" : "hidden"
					} ${isFocused ? "text-error-active" : "text-error-inactive"}`}
				>
					<div className="h-4 w-4 mr-2">{ErrorIcon(16)} </div>
					<div>{invalidMessage}</div>
				</div>
			</>
		);
	}
);

LongAnswerInput.displayName = "LongAnswerInput";

export default LongAnswerInput;
